/**
 * Library-free i18n registry for the Review step.
 * Renders human-readable sentences per mode + platform.
 */

import {
  MODE_META,
  SoftwareUpdateWizardState,
  SU_PLATFORM_META,
} from "../types";

export function summarizeHeader(s: SoftwareUpdateWizardState): string {
  return `${MODE_META[s.mode].label} · ${SU_PLATFORM_META[s.platform].label}`;
}

export function summarizePinned(s: SoftwareUpdateWizardState): string[] {
  const p = s.pinned;
  const lines: string[] = [];
  if (p.targetVersion) {
    lines.push(
      `Force ${SU_PLATFORM_META[s.platform].label} to version ${p.targetVersion}${
        p.targetBuild ? ` (build ${p.targetBuild})` : ""
      }.`,
    );
  }
  if (p.deadline) {
    try {
      lines.push(`Enforcement deadline: ${new Date(p.deadline).toLocaleString()} (device local time).`);
    } catch {
      lines.push(`Enforcement deadline: ${p.deadline} (device local time).`);
    }
  }
  if (p.detailsUrl) lines.push(`Details page: ${p.detailsUrl}`);
  return lines;
}

export function summarizeRolling(s: SoftwareUpdateWizardState): string[] {
  const r = s.rolling;
  const lane =
    r.lane === "latest"
      ? "Stay on the latest approved release"
      : r.lane === "n-1"
        ? "Stay one major behind (N-1)"
        : `Track major ${r.major || "—"}`;
  const lines = [
    `${lane}.`,
    `Apply ${r.delayDays} day${r.delayDays === 1 ? "" : "s"} after release at ${r.applyTime} (device local).`,
  ];
  if (!r.includeMinor && !r.includeMajor) lines.push("⚠ No release types selected.");
  if (r.snapshotVersion) lines.push(`Snapshot version: ${r.snapshotVersion}.`);
  return lines;
}

export function summarizeUpdateBehavior(s: SoftwareUpdateWizardState): string[] {
  const b = s.behavior;
  const lines: string[] = [];
  if (s.platform === "macos") {
    if (b.deferMajorDays) lines.push(`Defer major updates by ${b.deferMajorDays} days.`);
    if (b.deferMinorDays) lines.push(`Defer minor updates by ${b.deferMinorDays} days.`);
    if (b.deferSystemDays) lines.push(`Defer system/non-OS updates by ${b.deferSystemDays} days.`);
  } else if (b.deferCombinedDays) {
    lines.push(`Defer updates by ${b.deferCombinedDays} days.`);
  }
  if (b.recommendedCadence && s.platform !== "macos") {
    lines.push(`Recommended cadence: ${b.recommendedCadence}.`);
  }
  if (b.autoDownload !== "Allowed") lines.push(`Auto download: ${b.autoDownload}.`);
  if (b.autoInstallOs !== "Allowed") lines.push(`Auto install OS updates: ${b.autoInstallOs}.`);
  if (s.platform === "macos" && b.autoInstallSecurity !== "Allowed") {
    lines.push(`Auto install security updates: ${b.autoInstallSecurity}.`);
  }
  if (s.platform === "ios" || s.platform === "macos") {
    if (!b.rsrEnable) lines.push("Background Security Improvements: off.");
    if (!b.rsrEnableRollback) lines.push("Allow BSI rollback: off.");
  }
  if (s.platform === "macos" && !b.allowStandardUserOsUpdates) {
    lines.push("Standard users may not update.");
  }
  if (!b.notifications) lines.push("All update notifications suppressed.");
  if (b.betaEnrollment !== "Allowed") lines.push(`Beta enrollment: ${b.betaEnrollment}.`);
  if (lines.length === 0) lines.push("Use Apple defaults for every setting.");
  return lines;
}

export function summarize(s: SoftwareUpdateWizardState): string[] {
  if (s.mode === "pinned") return summarizePinned(s);
  if (s.mode === "rolling") return summarizeRolling(s);
  return summarizeUpdateBehavior(s);
}
